import { Box, Button, Dialog, DialogContent, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { FONT_ENUM } from '../../types/global/fonts.types';
import {
    BRAND_LUXE_PRIMARY,
    BRAND_LUXE_SECONDARY,
    BRAND_SURFACE_CONTAINER,
    BRAND_SURFACE_CONTAINER_HIGHEST,
} from './brandDashboardTokens';
import type { IInfluencer } from '../../redux/buzzbridge/types';
import { formatSocialHandle } from '../../utils/socialHandle';
import { influencerProfilePhotoCoverSx } from '../../components/BuzzBridge/utils/creatorProfileHelpers';
import { creatorPrimaryCategoryLabel } from '../../components/PublicInfluencerProfile/publicInfluencerProfileUtils';

export function formatFollowers(n?: number): string {
    if (!n) return '—';
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`;
    return String(n);
}

export function formatEngagement(rate?: number): string {
    if (rate === undefined || rate === null) return '—';
    return `${rate.toFixed(1)}%`;
}

export function getTotalFollowers(influencer: IInfluencer): number | undefined {
    if (influencer.totalReach) return influencer.totalReach;
    const sum =
        (influencer.instagram?.followers ?? 0) +
        (influencer.tiktok?.followers ?? 0) +
        (influencer.youtube?.followers ?? 0) +
        (influencer.facebook?.followers ?? 0) +
        (influencer.twitter?.followers ?? 0);
    return sum > 0 ? sum : undefined;
}

export interface CreatorProfileModalProps {
    influencer: IInfluencer | null;
    onClose: () => void;
    onInvite: () => void;
    /** Label for the invite button. Defaults to "Invite to campaign". */
    inviteLabel?: string;
    /** When true the button shows an "Invited" state. */
    invited?: boolean;
}

export function CreatorProfileModal({
    influencer,
    onClose,
    onInvite,
    inviteLabel = 'Invite to campaign',
    invited = false,
}: CreatorProfileModalProps) {
    if (!influencer) return null;

    const handle = influencer.instagram?.username
        ? formatSocialHandle(influencer.instagram.username)
        : influencer.tiktok?.username
        ? formatSocialHandle(influencer.tiktok.username)
        : influencer.youtube?.username
        ? formatSocialHandle(influencer.youtube.username)
        : null;

    const totalFollowers = getTotalFollowers(influencer);

    const allCategories: { main: string; sub?: string }[] = Array.isArray(influencer.categories)
        ? influencer.categories.map((c: any) =>
              typeof c === 'string' ? { main: c } : { main: c?.main ?? '', sub: c?.sub ?? '' },
          )
        : [];
    const categoryChips =
        allCategories.filter(c => Boolean(c.main?.trim())).length > 0
            ? allCategories.filter(c => Boolean(c.main?.trim()))
            : [{ main: creatorPrimaryCategoryLabel(influencer.categories) }];

    return (
        <Dialog
            open={Boolean(influencer)}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    bgcolor: '#111111',
                    borderRadius: { xs: 3, sm: 4 },
                    border: '1px solid rgba(255,255,255,0.07)',
                    overflow: 'hidden',
                    mx: { xs: 1.5, sm: 3 },
                },
            }}>
            {/* Header with close */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1.5, pb: 0 }}>
                <IconButton onClick={onClose} size="small" sx={{ color: 'rgba(255,255,255,0.4)' }}>
                    <CloseIcon sx={{ fontSize: 20 }} />
                </IconButton>
            </Box>

            <DialogContent
                sx={{
                    px: { xs: 2, sm: 3 },
                    pb: { xs: 2.5, sm: 3 },
                    pt: 0,
                    overflowY: 'auto',
                    '&::-webkit-scrollbar': { width: 0 },
                }}>
                {/* Profile image + name */}
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: { xs: 2.5, sm: 3 } }}>
                    <Box
                        sx={{
                            width: { xs: 80, sm: 110 },
                            height: { xs: 80, sm: 110 },
                            borderRadius: '50%',
                            overflow: 'hidden',
                            bgcolor: BRAND_SURFACE_CONTAINER_HIGHEST,
                            border: '3px solid rgba(255,201,101,0.2)',
                            mb: { xs: 1.5, sm: 2 },
                            flexShrink: 0,
                        }}>
                        {influencer.profile_picture ? (
                            <Box
                                component="img"
                                src={influencer.profile_picture}
                                alt={influencer.name ?? ''}
                                sx={{ width: '100%', height: '100%', ...influencerProfilePhotoCoverSx }}
                            />
                        ) : (
                            <Box
                                sx={{
                                    width: '100%',
                                    height: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}>
                                <Typography
                                    sx={{
                                        fontSize: { xs: 28, sm: 40 },
                                        color: 'rgba(255,255,255,0.15)',
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                    }}>
                                    {(influencer.name ?? '?')[0].toUpperCase()}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: { xs: 18, sm: 22 },
                            color: '#fff',
                            textAlign: 'center',
                        }}>
                        {influencer.name ?? 'Unknown'}
                    </Typography>
                    {handle && (
                        <Typography
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                fontSize: { xs: 12, sm: 14 },
                                color: '#adaaaa',
                                mt: 0.25,
                            }}>
                            {handle}
                        </Typography>
                    )}
                </Box>

                {/* Stats */}
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: { xs: 1, sm: 1.5 },
                        mb: { xs: 2.5, sm: 3 },
                    }}>
                    <Box sx={{ bgcolor: BRAND_SURFACE_CONTAINER, borderRadius: 2, p: { xs: 1.5, sm: 2 } }}>
                        <Typography sx={{ fontSize: { xs: 10, sm: 11 }, color: '#adaaaa', mb: 0.5 }}>
                            Followers
                        </Typography>
                        <Typography
                            sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: { xs: 20, sm: 24 }, color: '#fff' }}>
                            {formatFollowers(totalFollowers)}
                        </Typography>
                    </Box>
                    <Box sx={{ bgcolor: BRAND_SURFACE_CONTAINER, borderRadius: 2, p: { xs: 1.5, sm: 2 } }}>
                        <Typography sx={{ fontSize: { xs: 10, sm: 11 }, color: '#adaaaa', mb: 0.5 }}>
                            Eng. rate
                        </Typography>
                        <Typography
                            sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: { xs: 20, sm: 24 }, color: '#fff' }}>
                            {formatEngagement(influencer.avg_engagement_rate)}
                        </Typography>
                    </Box>
                </Box>

                {/* Categories */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: { xs: 2.5, sm: 3 } }}>
                    {categoryChips.map((cat, i) => (
                        <Box
                            key={i}
                            sx={{
                                px: { xs: 1.25, sm: 1.5 },
                                py: { xs: 0.5, sm: 0.75 },
                                borderRadius: 999,
                                bgcolor: 'rgba(255,255,255,0.07)',
                                border: '1px solid rgba(255,255,255,0.1)',
                            }}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    fontSize: { xs: 11, sm: 13 },
                                    color: '#fff',
                                }}>
                                {cat.main}
                                {cat.sub ? ` · ${cat.sub}` : ''}
                            </Typography>
                        </Box>
                    ))}
                </Box>

                {/* Bio */}
                {influencer.bio && (
                    <Box sx={{ mb: { xs: 2.5, sm: 3 } }}>
                        <Typography
                            sx={{
                                fontSize: 10,
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                letterSpacing: '0.12em',
                                textTransform: 'uppercase',
                                color: '#adaaaa',
                                mb: 1,
                            }}>
                            Bio
                        </Typography>
                        <Typography
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                fontSize: { xs: 13, sm: 15 },
                                color: 'rgba(255,255,255,0.75)',
                                lineHeight: 1.6,
                            }}>
                            {influencer.bio}
                        </Typography>
                    </Box>
                )}

                {/* Past work */}
                {(influencer.past_work?.length ?? 0) > 0 && (
                    <Box sx={{ mb: { xs: 2.5, sm: 3 } }}>
                        <Typography
                            sx={{
                                fontSize: 10,
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                letterSpacing: '0.12em',
                                textTransform: 'uppercase',
                                color: '#adaaaa',
                                mb: 1.5,
                            }}>
                            Past Work
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            {influencer.past_work!.map((work, i) => (
                                <Box
                                    key={work._id ?? i}
                                    sx={{
                                        bgcolor: BRAND_SURFACE_CONTAINER,
                                        borderRadius: 2,
                                        p: { xs: 2, sm: 2.5 },
                                        border: '1px solid rgba(255,255,255,0.05)',
                                    }}>
                                    <Typography
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            fontSize: { xs: 14, sm: 16 },
                                            color: '#fff',
                                            mb: 0.5,
                                        }}>
                                        {work.title ?? 'Untitled'}
                                    </Typography>
                                    {(work.year || work.type) && (
                                        <Typography sx={{ fontSize: { xs: 11, sm: 13 }, color: '#adaaaa', mb: 1 }}>
                                            {[work.year, work.type].filter(Boolean).join(' · ')}
                                        </Typography>
                                    )}
                                    {work.description && (
                                        <Typography
                                            sx={{
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                fontSize: { xs: 12, sm: 14 },
                                                color: 'rgba(255,255,255,0.6)',
                                                lineHeight: 1.6,
                                                mb: 1,
                                            }}>
                                            {work.description}
                                        </Typography>
                                    )}
                                    {work.link && (
                                        <Typography
                                            component="a"
                                            href={work.link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            sx={{
                                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                                fontSize: { xs: 12, sm: 13 },
                                                color: BRAND_LUXE_PRIMARY,
                                                textDecoration: 'none',
                                                '&:hover': { textDecoration: 'underline' },
                                            }}>
                                            Open link
                                        </Typography>
                                    )}
                                </Box>
                            ))}
                        </Box>
                    </Box>
                )}

                {/* Invite button */}
                <Button
                    fullWidth
                    onClick={onInvite}
                    sx={{
                        py: { xs: 1.5, sm: 1.75 },
                        borderRadius: 999,
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                        fontSize: { xs: 12, sm: 13 },
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        color: invited ? BRAND_LUXE_SECONDARY : '#392700',
                        background: invited
                            ? 'rgba(0,253,193,0.15)'
                            : `linear-gradient(to top right, ${BRAND_LUXE_PRIMARY}, #feb700)`,
                        border: invited ? '1px solid rgba(0,253,193,0.25)' : 'none',
                        '&:hover': invited
                            ? { background: 'rgba(0,253,193,0.22)' }
                            : { boxShadow: '0 0 20px rgba(255,201,101,0.35)', filter: 'brightness(1.05)' },
                    }}>
                    {invited ? 'Invited' : inviteLabel}
                </Button>
            </DialogContent>
        </Dialog>
    );
}
